import { getResourceHistory } from '@dispatch/shared/services';
import { isEmpty, isNil } from 'lodash';
import { useQuery } from '../useQuery';

export interface UseGetResourceHistoryParams {
  resourceId: string;
  from: string;
  to: string;
  /** CSV of `resource,schedule,event`. */
  types?: string;
  pageSize?: number;
  /** Caller privilege gate — usually `permissions?.can('view','resource-audit')`. */
  hasPermission?: boolean;
  /**
   * Extra gate for the query. When false the request is not fired (e.g. the
   * caller has deselected every change category, which should show nothing
   * rather than falling back to all categories server-side).
   */
  enabled?: boolean;
}

/**
 * Wraps the shared `useQuery` to fetch a resource's audit change history.
 *
 * The query only runs once a `resourceId`, a `from`/`to` range, and the
 * viewing privilege are all present, so the not-yet-deployed endpoint is
 * never hit speculatively.
 */
export const useGetResourceHistory = ({
  resourceId,
  from,
  to,
  types,
  pageSize,
  hasPermission = true,
  enabled = true,
}: UseGetResourceHistoryParams) => {
  return useQuery(
    ['resource-history', resourceId, from, to, types],
    () => getResourceHistory(resourceId, { from, to, types, pageSize }),
    {
      enabled:
        enabled &&
        Boolean(hasPermission) &&
        !isNil(resourceId) &&
        !isEmpty(resourceId) &&
        !isEmpty(from) &&
        !isEmpty(to),
      staleTime: 60000,
      keepPreviousData: true,
    }
  );
};
